import { Button, Level, SearchInput, Table } from '~/components';
import { useApi, useConfirmation } from '~/contexts';
import React, { useCallback, useEffect, useState } from 'react';
import { ErrorPage, PageLoader } from '~/routes/public/pages';
import styled from 'styled-components';
import moment from 'moment';
import { dateFormats } from '~/utils';
import { colors } from '~/styles';
import FeedbackDialog from './FeedbackDialog';
import { saveAs } from 'file-saver';

const PageArea = styled.div`
  display: flex;
  flex-direction: column;
`;

const Created = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const CreatedByName = styled.div`
  font-size: 0.875rem;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const CreatedByEmail = styled.div`
  color: ${colors.grey55};
  font-size: 0.75rem;
`;

const FeedbackContent = styled.p`
  flex-basis: 100%;
  padding: 0 4rem 1rem 12rem;
  color: ${colors.grey55};
  font-size: 0.75rem;
  white-space: pre-wrap;

  &:before {
    content: open-quote;
  }

  &:after {
    content: close-quote;
  }
`;

const FeedbackResponse = styled.div`
  flex-basis: 100%;
  padding: 0 4rem 0.25rem 12rem;
  color: ${colors.grey55};
  font-size: 0.75rem;
  font-style: italic;
`;

const FeedbackAction = styled.a`
  flex-basis: 100%;
  padding: 0 4rem 1rem 12rem;
  color: ${colors.primary};
  font-size: 0.75rem;

  &:hover {
    text-decoration: underline;
  }
`;

const AttachmentsList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.75rem;
`;

const AttachmentLink = styled.a`
  color: ${colors.primary};
  text-decoration: none;
  word-break: break-word;

  &:hover {
    text-decoration: underline;
  }
`;

const Deleted = styled.span`
  color: ${colors.grey55};
  font-size: 0.75rem;
  font-style: italic;
  margin-left: 0.35rem;
`;

function FeedbackItemsList() {
  const api = useApi();
  const confirmation = useConfirmation();

  const [query, setQuery] = useState({ isReady: false, data: null, error: null });
  const [isDownloading, setIsDownloading] = useState(false);
  const [params, setParams] = useState({ q: '' });

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.admin.feedbackItems.get(params);
      setQuery({ isReady: true, data });
    } catch (error) {
      setQuery({ isReady: true, data: null, error });
    }
  }, [params, api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = ({ target: { name, value } }) => {
    setParams((state) => ({ ...state, [name]: value }));
  };

  const handleSendEmail = async (feedbackItem) => {
    await confirmation.prompt((resolve) => (
      <FeedbackDialog
        feedbackItem={feedbackItem}
        onClose={resolve}
        onSaved={async () => {
          await fetchData();
          resolve();
        }}
      />
    ));
  };

  const handleDownload = async () => {
    setIsDownloading(true);

    try {
      const response = await api.admin.feedbackItems.export({ q: params.q });
      const disposition = response.headers['content-disposition'];
      const filenameMatch = disposition.match(/filename[^;=\n]*=((['"])(?<name>.*)?\2|[^;\n]*)/i);
      const filename = filenameMatch.groups['name'];
      const blob = new Blob([response.data], { type: 'text/csv' });

      saveAs(blob, filename);
    } finally {
      setIsDownloading(false);
    }
  };

  if (!query.isReady) return <PageLoader />;
  if (!query.data) return <ErrorPage publicSite={false} />;

  const data = query.data;

  return (
    <PageArea>
      <Level>
        <Level.Item>
          <SearchInput
            value={params.q}
            placeholder="Search"
            materialPlaceholder="Title"
            materialAlwaysVisible
            onChange={handleFilterChange}
          />
        </Level.Item>
        <Level.Item width="5rem" right>
          <Button isLoading={isDownloading} onClick={handleDownload}>
            Download CSV
          </Button>
        </Level.Item>
      </Level>
      <Table.Total value={data.length} label="Feedback Submission" style={{ marginTop: '1rem' }} />
      <Table>
        <Table.BoxHeader>
          <Table.Column width="10rem">Submitted On</Table.Column>
          <Table.Column width="12rem">Member</Table.Column>
          <Table.Column width="15rem">Workspace</Table.Column>
          <Table.Column width="20rem">Subject</Table.Column>
          <Table.Column align="right">Attachments</Table.Column>
        </Table.BoxHeader>
        <Table.Body>
          {data.map((item) => (
            <Table.Row key={item.id} style={{ flexWrap: 'wrap' }}>
              <Table.Cell>{moment(item.createdAt).format(dateFormats.compactDate)}</Table.Cell>
              <Table.Cell>
                {item.member ? (
                  <Created>
                    <CreatedByName>{item.member.name}</CreatedByName>
                    <CreatedByEmail>{item.member.email}</CreatedByEmail>
                  </Created>
                ) : (
                  <Created>
                    <CreatedByName>
                      {item.memberName}
                      <Deleted>deleted</Deleted>
                    </CreatedByName>
                    <CreatedByEmail>{item.memberEmail}</CreatedByEmail>
                  </Created>
                )}
              </Table.Cell>
              {item.workspace ? (
                <Table.Cell>{item.workspace.name}</Table.Cell>
              ) : (
                <Table.Cell>
                  {item.workspaceName}
                  <Deleted>deleted</Deleted>
                </Table.Cell>
              )}
              <Table.Cell>{item.subject}</Table.Cell>
              <Table.Cell align="right">
                <AttachmentsList>
                  {item.files?.map((file) => (
                    <li key={file.id}>
                      <AttachmentLink href={file.url} target="_blank" rel="noopener noreferrer">
                        {file.name}
                      </AttachmentLink>
                    </li>
                  ))}
                </AttachmentsList>
              </Table.Cell>

              <FeedbackContent>{item.content}</FeedbackContent>
              {item.responseSentAt ? (
                <>
                  <FeedbackResponse>
                    Responded by <strong>{item.responseSentByName}</strong> on{' '}
                    {moment(item.responseSentAt).format(dateFormats.dateTime)}
                  </FeedbackResponse>
                  <FeedbackContent>{item.responseContent}</FeedbackContent>
                </>
              ) : (
                item.member && <FeedbackAction onClick={() => handleSendEmail(item)}>Send Email</FeedbackAction>
              )}
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </PageArea>
  );
}

export default FeedbackItemsList;
